import { useCallback } from 'react';

import { useDeleteAnalyticalDatasetMutation } from '../lib/api/useDeleteAnalyticalDatasetMutation';

interface UseDeleteAnalyticalDatasetDialogArgs {
  projectId: string;
  analyticalDatasetProfileId: string;
  onClose: () => void;
}

export interface UseDeleteAnalyticalDatasetDialogResult {
  onSubmit: () => Promise<void>;
  closeDialog: () => void;
  isPending: boolean;
  submittingError: { message: string } | undefined;
  isValidToSubmit: boolean;
}

/**
 * Behaviour for the inline analytical-dataset delete confirmation on the
 * artifacts page: fires the delete mutation and surfaces its pending/error
 * state. Intl-free so the view owns copy and this stays unit-testable without
 * rendering the dialog. A 409 (run in progress) surfaces via `submittingError`.
 */
export const useDeleteAnalyticalDatasetDialog = ({
  projectId,
  analyticalDatasetProfileId,
  onClose,
}: UseDeleteAnalyticalDatasetDialogArgs): UseDeleteAnalyticalDatasetDialogResult => {
  const { mutateAsync, isPending, error, reset } = useDeleteAnalyticalDatasetMutation();

  const onSubmit = useCallback(async () => {
    await mutateAsync({ projectId, analyticalDatasetProfileId });
  }, [mutateAsync, projectId, analyticalDatasetProfileId]);

  const closeDialog = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return {
    onSubmit,
    closeDialog,
    isPending,
    submittingError: error ? { message: error.message } : undefined,
    isValidToSubmit: !isPending,
  };
};
